<template>
  <form @submit.prevent="onSubmit">
    <div class="card">
      <header class="card-header has-background-dark">
        <p class="card-header-title has-text-light">
          <span v-if="code">{{ code.code }}</span>
          <span v-else>CREATE CODE</span>
        </p>
        <div class="buttons" v-if="code">
          <b-button
            size="is-small"
            type="is-danger"
            :loading="deleting"
            @click="onRemove"
            >DELETE</b-button
          >
          <b-button size="is-small" @click="$emit('close')" v-if="code"
            >CLOSE</b-button
          >
        </div>
      </header>
      <div
        class="card-content scrollable"
        :style="{ height: `${scrollableHeight}px` }"
      >
        <base-select
          :service="$options.codeService"
          fetch-func="listTypes"
          label="Type"
          :value.sync="form.type"
          value-key="type"
          label-key="type"
        />
        <b-field label="Code">
          <b-input v-model="form.code"></b-input>
        </b-field>
        <b-field label="Name">
          <b-input v-model="form.name"></b-input>
        </b-field>
        <b-field label="Group">
          <b-input v-model="form.group"></b-input>
        </b-field>
        <b-field label="Complex">
          <b-switch :true-value="1" :false-value="0" v-model="form.complex" />
        </b-field>
        <div class="firms" v-if="code && code.type === 'PROBLEM'">
          <b-table
            :data="firms"
            :checked-rows.sync="form.firms"
            checkable
            :custom-is-checked="isFirmChecked"
          >
            <template slot-scope="props">
              <b-table-column field="name" label="ID">
                {{ props.row.name }}
              </b-table-column>
            </template>
          </b-table>
        </div>
      </div>
      <footer class="card-footer">
        <div class="buttons">
          <b-button
            class="is-fullwidth"
            native-type="submit"
            type="is-link"
            :loading="loading"
          >
            <span v-if="code">UPDATE</span>
            <span v-else>CREATE</span>
          </b-button>
        </div>
      </footer>
    </div>
  </form>
</template>

<script>
import BaseSelect from "@/components/partials/BaseSelect";
import { CodeService } from "@/services/code.service";
import { FirmService } from "@/services/firm.service";
import { JsonDenormalizer } from "@/helpers/JsonDenormalizer";

export default {
  codeService: CodeService,
  firmService: FirmService,
  props: {
    height: {
      type: Number,
      default: 500
    },
    code: {
      type: Object
    },
    firmFirmId: {
      type: [Number, String]
    },
    type: {
      type: String
    },
  },
  components: {
    BaseSelect
  },
  data: () => ({
    firms: [],
    form: {
      firms: []
    },
    loading: false,
    deleting: false
  }),
  computed: {
    scrollableHeight() {
      const spacesOnTop = 36 + 47; // card content padding, footer height
      return this.height - spacesOnTop;
    }
  },
  created() {
    this.fetchFirms();
  },
  watch: {
    code(code) {
      this.$nextTick(() => {
        if (code) {
          this.form = {
            code: code.code,
            name: code.name,
            group: code.group,
            type: code.type,
            complex: code.complex,
            firms: code.firms
          };
        } else {
          this.reset();
        }
      });
    },
    type(type) {
      this.$set(this.form, "type", type || "");
    },
  },
  methods: {
    async fetchFirms() {
      try {
        this.firms = await this.$options.firmService
          .all()
          .then(response => JsonDenormalizer.denormalize(response));
      } catch (err) {}
    },
    isFirmChecked(checked, compare) {
      return checked.id === compare.id;
    },
    transformedData() {
      return {
        code: this.form.code,
        name: this.form.name,
        group: this.form.group,
        type: this.form.type,
        complex: this.form.complex,
        firm_ids: this.form.firms.map(firm => firm.id),
      };
    },
    reset() {
      this.form = {
        code: "",
        name: "",
        group: "",
        type: this.type || "",
        complex: 0,
        firms: []
      };
    },
    onSubmit() {
      if (this.code) {
        this.update();
      } else {
        this.create();
      }
    },
    async create() {
      if (!this.firmFirmId) {
        this.$buefy.toast.open({
          message: "firm_firm_id is required.",
          type: "is-danger",
          position: "is-top-right"
        });
        return;
      };

      this.loading = true;

      try {
        const response = await this.$options.codeService.create({
          ...this.transformedData(),
          firm_firm_id: this.firmFirmId,
          includes: "firms,childCodes"
        });

        const code = JsonDenormalizer.denormalize(response);

        this.$buefy.toast.open({
          message: "Code has been created.",
          type: "is-info",
          position: "is-top-right"
        });
        this.reset();
        this.$emit("create-success", code);
      } catch (err) {}

      this.loading = false;
    },
    async update() {
      this.loading = true;

      try {
        const response = await this.$options.codeService.update(this.code.id, {
          ...this.transformedData(),
          includes: "firms,childCodes"
        });

        const code = JsonDenormalizer.denormalize(response);

        this.$buefy.toast.open({
          message: "Code has been updated.",
          type: "is-info",
          position: "is-top-right"
        });
        // this.reset();
        this.$emit("update-success", code);
      } catch (err) {}

      this.loading = false;
    },
    onRemove() {
      this.$buefy.dialog.confirm({
        title: "Confirm",
        message: "Are you sure you want to <b>delete</b> this code?",
        confirmText: "Delete",
        type: "is-danger",
        hasIcon: true,
        onConfirm: () => {
          this.remove();
        }
      });
    },
    async remove() {
      this.deleting = true;

      try {
        await this.$options.codeService.delete(this.code.id);

        this.$buefy.toast.open({
          message: "Code has been deleted.",
          type: "is-info",
          position: "is-top-right"
        });
        this.$emit("remove-success");
      } catch (err) {}

      this.deleting = false;
    }
  }
};
</script>

<style lang="scss">
.firms {
  .table thead {
    display: none;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/pages/Codes/list/partials/form.vue